import { Badge } from '@web-app/components/ui/Badge/badge';
import { formatHealthieDisplayDate } from '@web-app/utils/date-time.utils';
import { formatHeight } from '@web-app/utils/format.utils';
import {
  GLP1_FOLLOW_UP_VISIT_SNAPSHOT_VERSION,
  type Glp1FollowUpCheckInSnapshot,
  type Glp1FollowUpSnapshot,
  type Glp1FollowUpVisitSnapshot,
} from '@web-app/utils/glp1/follow-up-snapshot';
import { format, isValid, parseISO } from 'date-fns';
import { ReactNode } from 'react';

type FieldRow = {
  label: string;
  value: ReactNode;
};

type Section = {
  title: string;
  rows: FieldRow[];
};

const formatSnakeCase = (value: string): string => value.replace(/_/g, ' ');

const VISIT_REASON_LABELS: Record<string, string> = {
  refills: 'Refills',
  symptoms: 'Symptoms',
  other_concerns: 'Other concerns',
};

const NotRecorded = ({ children }: { children: ReactNode }): JSX.Element => (
  <span className="text-muted-foreground">{children}</span>
);

const formatSubmittedAt = (iso: string): string | null => {
  const date = parseISO(iso);
  if (!isValid(date)) return null;
  return `Submitted ${format(date, 'MMM d, yyyy')} • ${format(date, 'h:mm a')}`;
};

const YesNoValue = ({ value }: { value: boolean }): JSX.Element =>
  value ? (
    <Badge variant="ghost" className="bg-primary/10 text-primary">
      Yes
    </Badge>
  ) : (
    <span className="text-sm font-medium text-foreground">No</span>
  );

const FieldRowView = ({ label, value }: FieldRow): JSX.Element => (
  <div className="grid grid-cols-[minmax(0,160px)_minmax(0,1fr)] items-start gap-x-4 py-1">
    <span className="text-sm leading-5 text-muted-foreground">{label}</span>
    <div className="min-w-0 text-sm font-medium leading-5 text-foreground">
      {value}
    </div>
  </div>
);

const buildVisitSections = ({
  answers,
}: Glp1FollowUpVisitSnapshot): Section[] => [
  {
    title: 'Visit',
    rows: [
      {
        label: 'Reason for visit',
        value:
          VISIT_REASON_LABELS[answers.reason] ??
          formatSnakeCase(answers.reason),
      },
    ],
  },
  {
    title: 'Patient information',
    rows: [
      {
        label: 'Current weight',
        value: (
          <span className="font-bold">{answers.currentWeightLbs} lbs</span>
        ),
      },
      {
        label: 'Height',
        value:
          answers.heightInches != null ? (
            formatHeight(answers.heightInches)
          ) : (
            <NotRecorded>Not on record</NotRecorded>
          ),
      },
      {
        label: 'BMI',
        value:
          answers.bmi != null ? (
            answers.bmi
          ) : (
            <NotRecorded>Not calculated (no height on record)</NotRecorded>
          ),
      },
    ],
  },
  ...(answers.otherConcernsText != null
    ? [
        {
          title: 'Discussion',
          rows: [
            {
              label: 'What to discuss',
              value: answers.otherConcernsText,
            },
          ],
        },
      ]
    : []),
];

const buildCheckInSections = ({
  answers,
}: Glp1FollowUpCheckInSnapshot): Section[] => [
  {
    title: 'Patient information',
    rows: [
      {
        label: 'Current weight',
        value: (
          <span className="font-bold">{answers.currentWeightLbs} lbs</span>
        ),
      },
    ],
  },
  {
    title: 'Medication',
    rows: [
      {
        label: 'Has started medication',
        value: <YesNoValue value={answers.hasStartedMedication} />,
      },
      ...(answers.firstDoseDate != null
        ? [
            {
              label: 'First dose date',
              value:
                formatHealthieDisplayDate(answers.firstDoseDate) ??
                answers.firstDoseDate,
            },
          ]
        : []),
    ],
  },
  {
    title: 'Symptoms',
    rows: [
      {
        label: 'GI symptoms',
        value:
          answers.giSymptoms.length === 0 ? (
            <span className="text-muted-foreground">None reported</span>
          ) : (
            answers.giSymptoms.map(formatSnakeCase).join(', ')
          ),
      },
      ...(answers.symptomSeverity != null
        ? [
            {
              label: 'Symptom severity',
              value: formatSnakeCase(answers.symptomSeverity),
            },
          ]
        : []),
      {
        label: 'Severe abdominal pain',
        value: <YesNoValue value={answers.severeAbdominalPain} />,
      },
      {
        label: 'Mood changes',
        value: <YesNoValue value={answers.moodChanges} />,
      },
      ...(answers.urgentNeeds != null
        ? [
            {
              label: 'Urgent need to see physician',
              value: <YesNoValue value={answers.urgentNeeds} />,
            },
          ]
        : []),
    ],
  },
  ...(answers.additionalNotes != null
    ? [
        {
          title: 'Additional notes',
          rows: [{ label: 'Notes', value: answers.additionalNotes }],
        },
      ]
    : []),
];

interface Glp1FollowUpNoteBodyProps {
  snapshot: Glp1FollowUpSnapshot;
}

/** Section-rail card for a GLP-1 follow-up snapshot. */
export const Glp1FollowUpNoteBody = ({
  snapshot,
}: Glp1FollowUpNoteBodyProps): JSX.Element => {
  const sections =
    snapshot.v === GLP1_FOLLOW_UP_VISIT_SNAPSHOT_VERSION
      ? buildVisitSections(snapshot)
      : buildCheckInSections(snapshot);
  const responseCount = sections.reduce((n, s) => n + s.rows.length, 0);
  const submittedLabel = formatSubmittedAt(snapshot.submittedAt);

  return (
    <div
      className="rounded-md border border-border bg-card p-4"
      data-testid="glp1-follow-up-note-body"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        {submittedLabel ? (
          <p className="text-[13px] text-muted-foreground">{submittedLabel}</p>
        ) : (
          <span />
        )}
        <Badge variant="ghost" className="shrink-0 bg-primary/10 text-primary">
          {responseCount} {responseCount === 1 ? 'response' : 'responses'}
        </Badge>
      </div>

      <div className="divide-y divide-border">
        {sections.map((section) => (
          <div
            key={section.title}
            className="grid grid-cols-[minmax(0,140px)_minmax(0,1fr)] items-start gap-x-4 py-3"
          >
            <p className="pt-0.5 text-[13px] font-bold text-primary">
              {section.title}
            </p>
            <div className="flex min-w-0 flex-col gap-1">
              {section.rows.map((row) => (
                <FieldRowView key={row.label} {...row} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
